import React from "react";

import classnames from "classnames";

import SegmentedControl from "./SegmentedControl/SegmentedControl";
import { DEFAULT_MOST_VIEWED_ITEM_TYPE } from "../../constants/mostViewed";
import { SMARTLIST_TAB } from "../../types/platform";
import {
  isCarouselComponent,
  isFastNowSmartlist,
  isMosaicComponent,
  isMostViewedSmartlist,
  isMyListComponent,
  isPurchaseComponent,
  isResumeComponent,
  isSmartlistDataSourceComponent,
  isUserRecommendationComponent,
} from "../../utils/typePredicate";
import FastNowSelect from "../platform/smartlistSources/FastNowSelect";
import MostViewedSelect from "../platform/smartlistSources/MostViewedSelect";
import SmartlistSelect from "../platform/smartlistSources/SmartlistSelect";

import type { JSX } from "react";
import type { SingleValue } from "react-select";

import type {
  ComponentWithDataSourceParams,
  ComponentWithUnknownDataSourceParams,
  MostViewedParams,
  NowComponentParams,
  SimpleModuleComponent,
  SimpleModuleName,
  SmartlistModuleName,
} from "../../api/ProjectAPI.types";
import type { SmartlistDataSourceTab } from "../../types/platform";
import type { SmartlistOption } from "../platform/smartlistSources/SmartlistSelect";

export interface StaticComponentStructure {
  smartlist_module_name: SmartlistModuleName;
  section_uuid?: string | null;
  uuid?: string;
}

interface SmartlistDataSourceProps {
  hasSectionSeparator?: boolean;
  label?: string;
  tabs: SmartlistDataSourceTab[];
  item: ComponentWithUnknownDataSourceParams;
  onComponentUpdate: (item: ComponentWithUnknownDataSourceParams) => void;
}

function SmartlistDataSource({
  hasSectionSeparator,
  label,
  tabs,
  item,
  onComponentUpdate,
}: SmartlistDataSourceProps): JSX.Element | null {
  if (!item) return null;

  const createSmartlistItem = (): ComponentWithUnknownDataSourceParams => ({
    ...item,
    dynamic_component: {
      smartlist_uuid: "",
      uuid: "",
      section_uuid: null,
    },
    static_component: null,
  });

  const createStaticModuleItem = (
    moduleName: SimpleModuleName,
  ): SimpleModuleComponent | null => {
    const updatedItem = {
      ...item,
      dynamic_component: null,
      static_component: {
        smartlist_module_name: moduleName,
        section_uuid: null,
        uuid: undefined,
      },
    };

    if (
      isMyListComponent(updatedItem) ||
      isResumeComponent(updatedItem) ||
      isUserRecommendationComponent(updatedItem) ||
      isPurchaseComponent(updatedItem)
    ) {
      return updatedItem;
    }

    return null;
  };

  const createNowSmartlistItem =
    (): ComponentWithDataSourceParams<NowComponentParams> | null => {
      if (isCarouselComponent(item)) {
        return {
          ...item,
          dynamic_component: null,
          static_component: {
            smartlist_module_name: "now",
          },
          params: {
            ...item.params,
            display_dialog: false,
            display_order: undefined,
            themeId: "all",
          },
        };
      }

      if (isMosaicComponent(item)) {
        return {
          ...item,
          dynamic_component: null,
          static_component: {
            smartlist_module_name: "now",
          },
          params: {
            ...item.params,
            display_dialog: false,
            display_order: undefined,
            themeId: "all",
          },
        };
      }

      return null;
    };

  const createMostViewedSmartlistItem =
    (): ComponentWithDataSourceParams<MostViewedParams> | null => {
      if (isCarouselComponent(item)) {
        return {
          ...item,
          dynamic_component: null,
          static_component: {
            smartlist_module_name: "mostViewed",
          },
          params: {
            ...item.params,
            itemType: DEFAULT_MOST_VIEWED_ITEM_TYPE,
            period: "week",
            maxItems: 10,
            hasNumberOnItems: false,
          },
        };
      }

      if (isMosaicComponent(item)) {
        return {
          ...item,
          dynamic_component: null,
          static_component: {
            smartlist_module_name: "mostViewed",
          },
          params: {
            ...item.params,
            itemType: DEFAULT_MOST_VIEWED_ITEM_TYPE,
            period: "week",
            maxItems: 10,
            hasNumberOnItems: false,
          },
        };
      }

      return null;
    };

  const datasourceCreatorMap: Record<
    string,
    () => ComponentWithUnknownDataSourceParams | null
  > = {
    smartlist: createSmartlistItem,
    my_list: () => createStaticModuleItem("my_list"),
    resume: () => createStaticModuleItem("resume"),
    user_recommendation: () => createStaticModuleItem("user_recommendation"),
    purchase: () => createStaticModuleItem("purchase"),
    now: createNowSmartlistItem,
    mostViewed: createMostViewedSmartlistItem,
  };

  const handleSwitchDatasource = (value: number): void => {
    const tab = tabs.find((tabItem) => tabItem.value === value);

    if (!tab?.type) return;

    const createUpdatedItem = datasourceCreatorMap[tab.type];

    if (!createUpdatedItem) {
      onComponentUpdate(item);
      return;
    }

    const updatedItem = createUpdatedItem();

    if (!updatedItem) return;

    onComponentUpdate(updatedItem);
  };

  const getCurrentDatasourceIndex = (): number => {
    const tab = tabs.find(
      (tabItem: SmartlistDataSourceTab) =>
        item?.static_component?.smartlist_module_name === tabItem?.type,
    );

    return tab?.value ?? SMARTLIST_TAB.value;
  };

  const handleThemeChange = (
    selectedOption: SingleValue<{ value: string }>,
  ): void => {
    if (selectedOption) {
      const updatedComponent = {
        ...item,
        params: {
          ...item.params,
          themeId: selectedOption.value,
        },
      };
      onComponentUpdate(
        updatedComponent as ComponentWithUnknownDataSourceParams,
      );
    }
  };

  const handleSmartlistSelection = (selection: SmartlistOption): void => {
    const updatedComponent = {
      ...item,
      dynamic_component: {
        smartlist_uuid: selection.value,
      },
    };
    onComponentUpdate(updatedComponent as ComponentWithUnknownDataSourceParams);
  };

  return (
    <div
      className={classnames("editor-form-section", {
        "section-separator": hasSectionSeparator,
      })}
    >
      <div className="editor-form-row">
        {label ? (
          <div className="editor-form-section-title">{label}</div>
        ) : null}

        <div className="editor-form-input overflow-auto">
          <SegmentedControl
            onChange={handleSwitchDatasource}
            selected={getCurrentDatasourceIndex()}
            tabs={tabs}
          />
        </div>
      </div>
      {isSmartlistDataSourceComponent(item) && (
        <SmartlistSelect item={item} onChange={handleSmartlistSelection} />
      )}
      {isFastNowSmartlist(item) && (
        <FastNowSelect item={item} onChange={handleThemeChange} />
      )}
      {isMostViewedSmartlist(item) && (
        <MostViewedSelect item={item} onChange={onComponentUpdate} />
      )}
    </div>
  );
}

export default SmartlistDataSource;
